import { useState, useEffect, createContext, useContext } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { updateVisite, createVisite, emptyVisite } from './visitesSlice'
import { Input, Textarea } from '@/components/ui'
import { useBienContext } from '../biens/BienContext'
import useFormConfirmation from '../../hooks/useFormConfirmation'

export default ({ onClose = () => {}, onSuccess = () => {} }) => {
  const dispatch = useDispatch()
  const { bien } = useBienContext()
  const visite = useSelector((state) => state.visites.current || emptyVisite)
  const errors = useSelector((state) => state.visites.requestStatus.errors)
  const isCreating = useSelector((state) => state.visites.loadingStatus.create)
  const isUpdating = useSelector((state) => state.visites.loadingStatus.update)
  const isLoading = isCreating || isUpdating
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { handleSubmit, watch, 
    setValue, getValues, 
    reset,
    control, formState: { errors: formErrors }
  } = useForm({
    defaultValues: visite,
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const onReset = () => {
    reset(visite)
  }

  // useFormConfirmation feature
  // ajoute les states, le useEffect pour surveiller l'état du formulaire
  // et s'assurer s'il a été modifié ou pas pour afficher une boîte de 
  // confirmation lorsque le bouton "Annuler" est cliqué.
  const {
    handleFormCancel,
    setInitialLoadComplete,
    setInitialValues,
    setFormIsDirty,
    FormConfirmDialog
  } = useFormConfirmation({
    watch,
    getValues,
    onReset,
    onClose
  })

  // Synchroniser le formulaire lorsque visite change 
  useEffect(() => {
    if (!visite) return;
    setValue("date_visite", visite.date_visite)
    setValue("commentaires", visite.commentaires) 

    // useFormConfirmation feature
    // Marquer le chargement initial comme terminé et enregistrer les valeurs initiales
    // Utiliser un délai pour s'assurer que le formulaire est complètement mis à jour
    setTimeout(() => {
      setInitialValues(getValues())
      setInitialLoadComplete(true)
      setFormIsDirty(false) // Réinitialiser l'état dirty après le chargement initial
    }, 100)
  }, [visite]) 

  // Effet pour détecter les changements dans l'état de chargement
  useEffect(() => {
    if (isSubmitting && !isUpdating && !isCreating) {
      setIsSubmitting(false)
      if (!errors || Object.keys(errors).length === 0) {
        onSuccess()
      }
    }
  }, [isUpdating, isCreating, errors, isSubmitting, onSuccess, dispatch])

  /**
   * Récupère le message d'erreur pour un champ donné
   * Combine les erreurs de validation côté client et les erreurs API
   *
   * @param {string} fieldName - Nom du champ
   * @returns {string} Message d'erreur ou chaîne vide
   */
  const getErrorMessage = (fieldName) =>
    formErrors?.[fieldName]?.message || errors?.[fieldName] || ''

  /**
   * Détermine si un champ a une erreur
   *
   * @param {string} fieldName - Nom du champ
   * @returns {boolean} true si le champ a une erreur, false sinon
   */
  const hasError = (fieldName) =>
    !!formErrors?.[fieldName] || !!errors?.[fieldName]

  /**
   * Gère la soumission du formulaire
   * Dispatche l'action appropriée (create ou update) selon si le bien existe déjà
   *
   * @param {Object} formData - Données du formulaire
   */
  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true)

      if (visite.id) {
        await dispatch(updateVisite({bien_id: bien.id, id: visite.id, data: formData}))
      } else {
        await dispatch(createVisite({...formData, bien_id: bien.id}))
      }
    } catch (error) {
      setIsSubmitting(false)
    }
  }

  // Set visite current ou emptyVisite
  // s'inspirer du formulaire d'un bien
  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">
            {visite.id ? 'Modifier la visite' : 'Ajouter une visite'}
          </h2>
        </div>

        <div className="card bg-base-100 border border-base-200 rounded-none mb-4 min-h-full">
          <div className="p-4">
            <div className="card-body p-4">
              <div className={`flex space-x-4`}>
                <div className='space-y-4 mt-4'>
                  <Input.Date
                    control={control}
                    name="date_visite"
                    label="Date de la visite" 
                    validator={{hasError, getErrorMessage}}
                  />
                </div>
                <div className='space-y-4 w-1/2'>
                  <Textarea.Form control={control} name="commentaires" label="Commentaires" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between">
          <button
            type="button"
            className="btn btn-outline"
            onClick={handleFormCancel}
          >
            Annuler
          </button>
          <button
            type="submit"
            className={`btn ${isLoading ? 'loading' : ''} btn-primary text-white p-2`}
            disabled={isLoading}
          >
            {isLoading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </form>
      <FormConfirmDialog />
    </div>
  )
}